import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Tooltip,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { API_ENDPOINTS } from "config/api";

const DEFAULT_PRIVACY = {
  id: 0,
  studentId: 0,
  isChildrenVisible: false,
  isWorkingDetailsVisible: false,
  isSocialMediaLinksVisible: false,
  isMyExpertiseVisible: false,
};

const ProfilePrivacySettings = ({ studentId, token }) => {
  const [privacy, setPrivacy] = useState(null);
  const [isNew, setIsNew] = useState(false); // Track new insert
  const [editMode, setEditMode] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchPrivacySettings = () => {
    axios
      .get(API_ENDPOINTS.PROFILE.GET_PROFILE_PRIVACY_SETTINGS(studentId), {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data) {
          setPrivacy(res.data);
          setIsNew(false);
        } else {
          setPrivacy({ ...DEFAULT_PRIVACY, studentId });
          setIsNew(true);
        }
      })
      .catch((err) => {
        console.warn("Privacy settings not found, initializing new.", err);
        setPrivacy({ ...DEFAULT_PRIVACY, studentId });
        setIsNew(true);
      });
  };

  useEffect(() => {
    if (studentId && token) fetchPrivacySettings();
  }, [studentId, token]);

  const handleSavePrivacy = async () => {
    try {
      const res = await axios.post(
        API_ENDPOINTS.PROFILE.UPDATE_PROFILE_PRIVACY_SETTINGS,
        {
          ...privacy,
          id: isNew ? 0 : privacy.id, // Send 0 for insert
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setSnackbar({
        open: true,
        message: res.data.message || "Privacy settings saved.",
        severity: "success",
      });
      setEditMode(false);
      fetchPrivacySettings(); // Refresh after save
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to save settings.", severity: "error" });
      console.error(err);
    }
  };

  const toggleVisibility = (field) => {
    if (!editMode) return;
    setPrivacy((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const renderRow = (label, field, tooltip) => (
    <Box display="flex" alignItems="center" justifyContent="space-between" px={1}>
      <Tooltip title={tooltip} arrow>
        <Typography fontSize={14}>{label}</Typography>
      </Tooltip>
      <Tooltip title={privacy[field] ? "Currently visible" : "Currently hidden"} arrow>
        <span>
          <IconButton onClick={() => toggleVisibility(field)} size="small" disabled={!editMode}>
            {privacy[field] ? <VisibilityIcon color="info" /> : <VisibilityOffIcon color="disabled" />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );

  if (!privacy) return <Typography>Loading privacy settings...</Typography>;

  return (
    <Box mt={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">
          Privacy Settings {isNew && "(No Profile)"}
        </Typography>
        {!editMode ? (
          <Tooltip title="Edit Privacy Settings" arrow>
            <IconButton onClick={() => setEditMode(true)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Save Changes" arrow>
            <IconButton onClick={handleSavePrivacy}>
              <SaveIcon color="success" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box display="flex" flexDirection="column" gap={1}>
        {renderRow("Children Visible", "isChildrenVisible", "Toggle children information visibility")}
        {renderRow("Working Details Visible", "isWorkingDetailsVisible", "Toggle working details visibility")}
        {renderRow("Social Media Links Visible", "isSocialMediaLinksVisible", "Toggle social media visibility")}
        {renderRow("My Expertise Visible", "isMyExpertiseVisible", "Toggle expertise visibility")}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }} elevation={6} variant="filled">
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

ProfilePrivacySettings.propTypes = {
  studentId: PropTypes.number.isRequired,
  token: PropTypes.string.isRequired,
};

export default ProfilePrivacySettings;
